import './I9Training.scss';

import { Alert, Checkbox, SkipNav, Button as USWDSButton } from '@ver-uds/uswds-react';
import { Button, ButtonGroup, PageHeader } from '@ver-uds/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthState, useLogout } from '@vis-auth/react';
import { User, MyI9TrainingStatus } from '@vis-auth/vis-user-client';

import { EveRoutes } from '@ver-uds/e-verify';
import { Helmet } from 'react-helmet-async';
import ReactPlayer from 'react-player/lazy';
import I9CancelModal from '../I9CancelModal/I9CancelModal';
import I9Constants from '../I9Constants/I9Constants';
import Divider from '../Divider/Divider';
import dhsSeal from '../../images/dhs-seal.svg';
import useUserApi from '../../services/UserApi';
import I9TrainingVideo from '../I9TrainingVideo/I9TrainingVideo';
import VideoUnavailableLarge from '../../images/VideoUnavailable/VideoUnavailableLarge.png';
import TroubleshootingModal from '../I9TrainingVideo/TroubleshootingModal/TroubleshootingModal';
import { addNewRelicCustomAttribute } from '../../utils/new-relic-help';
import { useUser } from '../../context/User/UserContext';

const I9Training = (): JSX.Element => {
  const header = 'Training and Attestation';
  const userApi = useUserApi();
  const { user } = useAuthState();
  const id = user?.user_id as string;
  const { accountId, logonId } = useUser() as User;

  const [isSending, setIsSending] = useState<boolean>(false);
  const [canAttest, setCanAttest] = React.useState<boolean>(false);
  const [userAttested, setUserAttested] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [showSystemError, setShowSystemError] = useState<boolean>(false);
  const [i9CancelModalIsOpen, setI9CancelModalIsOpen] = useState<boolean>(false);
  const isMounted = useRef(true);
  const logout = useLogout();
  const videoRef = useRef<ReactPlayer>(null);
  const backupVideoRef = useRef<ReactPlayer>(null);
  const [played, setPlayed] = useState(0);
  const [showTroubleshootingModal, setShowTroubleshootingModal] = useState<boolean>(false);
  const [forceBackupVideo, setForceBackupVideo] = useState<boolean>(false);

  const handleVideoEnded = (): void => {
    setCanAttest(true);
    setShowError(false);
  };

  const validateCheckbox = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.stopPropagation();
    if (canAttest) {
      setUserAttested(e.target.checked);
    } else {
      setUserAttested(false);
      setShowError(true);
    }
  };

  // set isMounted to false when we unmount the component
  useEffect(() => {
    return (): void => {
      isMounted.current = false;
    };
  }, []);

  const handleCancelClick = (): void => {
    setI9CancelModalIsOpen(true);
  };

  const handleProgress = (activeVideoRef: React.RefObject<ReactPlayer> | undefined): void => {
    if (activeVideoRef?.current) {
      if (activeVideoRef.current.getCurrentTime() >= played + 3) {
        // fast forward, send back
        activeVideoRef.current.seekTo(played);
      } else if (activeVideoRef.current.getCurrentTime() >= played) {
        setPlayed(activeVideoRef.current.getCurrentTime());
      }
    }
  };

  const handleClick = useCallback(async () => {
    if (isSending) {
      return;
    }

    if (canAttest && userAttested) {
      setIsSending(true);
      setShowSystemError(false);

      // submit the data
      try {
        const response = await userApi.myI9TrainingCompleted({ id });
        if (response.myI9TrainingStatus === MyI9TrainingStatus.COMPLETE) {
          // Assign value to a key
          sessionStorage.setItem('I9TrainingCompleted', 'Y');
          window.location.assign(EveRoutes.HOME);
        } else {
          // need to show an error
          setShowSystemError(true);
        }
      } catch (e) {
        setShowSystemError(true);
      } finally {
        if (isMounted.current) {
          // only update if we are still mounted
          setIsSending(false);
        }
      }
    } else {
      setShowError(true);
    }
  }, [canAttest, isSending, userApi, userAttested, id]);

  const openTroubleshootingModal = () => {
    setShowTroubleshootingModal(true);
    addNewRelicCustomAttribute('VIEWING_TRAINING_TROUBLESHOOTING_INSTRUCTIONS_USER_ID', logonId);
    addNewRelicCustomAttribute('VIEWING_TRAINING_TROUBLESHOOTING_INSTRUCTIONS_ACCOUNT_ID', accountId);
  };

  return (
    <>
      <Helmet>
        <title>{I9Constants.PROGRAM_NAME} Training</title>
      </Helmet>
      <PageHeader>{header}</PageHeader>
      <SkipNav.MainContent>
        <div className="app grid-container height-full">
          <div className="grid-row grid-gap">
            <main className="grid-row grid-col-12 padding-top-4" data-testid="i9-training-container">
              <div className="sm-grid-col-12 grid-col-9 padding-bottom-3">
                <h1 className="margin-bottom-2">Your company has enabled {I9Constants.PROGRAM_NAME}</h1>
                <p className="usa-intro margin-top-2">{I9Constants.I9_INTRO_TEXT}</p>
                <p className="margin-bottom-4 margin-top-3">
                  Watch the {I9Constants.PROGRAM_NAME} training video on this page to learn more. Once completed, select
                  agree to access E-Verify.
                </p>
                <I9TrainingVideo
                  videoRef={videoRef}
                  backupVideoRef={backupVideoRef}
                  handleVideoEnded={handleVideoEnded}
                  handleProgress={handleProgress}
                  className="padding-bottom-2"
                  videoUnavailableSrc={VideoUnavailableLarge}
                  forceBackupVideo={forceBackupVideo}
                />

                <Alert status="info">
                  <Alert.Heading>{I9Constants.PROGRAM_NAME} Training and Attestation</Alert.Heading>
                  <Alert.Text>
                    I certify that I have completed the required {I9Constants.PROGRAM_NAME} training. I am aware that
                    providing any false information may be grounds for suspending or terminating participation in
                    E-Verify and {I9Constants.PROGRAM_NAME}.
                    <br />
                  </Alert.Text>
                  <Checkbox
                    id="iAgreeCheckbox"
                    data-testid="i-agree-checkbox"
                    className="margin-bottom-2 width-15 bg-transparent"
                    name="iAgreeCheckbox"
                    label="I agree"
                    checked={userAttested}
                    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
                    onChange={(e) => validateCheckbox(e)}
                  />
                </Alert>
                {showError && (
                  <Alert status="error">
                    <Alert.Text>
                      You must view the training video on this page and attest to completion by checking the checkbox
                      above before proceeding to E-Verify.
                    </Alert.Text>
                  </Alert>
                )}
                {showSystemError && (
                  <Alert status="error">
                    <Alert.Text>
                      Your training attestation is unable to be processed at this time. Please wait a few minutes and
                      try again.
                    </Alert.Text>
                  </Alert>
                )}
              </div>
              <img
                className="us-department-of-homeland-security-logo-png-transparent"
                src={dhsSeal}
                alt="Department of Homeland Security (DHS) Seal"
              />
              <div className="grid-col-12 padding-bottom-6">
                <p className="padding-bottom-2">
                  Having trouble with training or attestation?{' '}
                  <USWDSButton
                    className="margin-left-05"
                    variant="unstyled"
                    onClick={openTroubleshootingModal}
                    aria-label="Click here to open a modal explaining troubleshooting instructions"
                  >
                    Click here
                  </USWDSButton>
                </p>
                <Divider />
                <ButtonGroup className="margin-top-4 training-button-group">
                  <Button variant="secondary" disabled={isSending} onClick={handleCancelClick} aria-label="Cancel">
                    Cancel
                  </Button>
                  <Button
                    key="Continue to E-Verify Home"
                    variant="primary"
                    disabled={isSending}
                    onClick={handleClick}
                    aria-label="Continue to E-Verify Home"
                  >
                    Continue to E-Verify Home
                  </Button>
                </ButtonGroup>
              </div>
            </main>
          </div>
        </div>
        <I9CancelModal
          isOpen={i9CancelModalIsOpen}
          close={(): void => {
            setI9CancelModalIsOpen(false);
          }}
          onLogout={logout}
        />
        <TroubleshootingModal
          isOpen={showTroubleshootingModal}
          close={(): void => {
            setShowTroubleshootingModal(false);
            setForceBackupVideo(true);
          }}
        />
      </SkipNav.MainContent>
    </>
  );
};

export default I9Training;
